
import { FC, useMemo } from 'react';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-clock/dist/Clock.css';
import './datePickerStacking.css';
import Picker from 'react-datetime-picker';
import { format, isBefore, isValid, parse } from 'date-fns';
import { InputProps } from './input.types';
import { CalendarDotsIcon } from '@phosphor-icons/react';
import { useIntl } from 'react-intl';

const DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm";

const DateTimePicker: FC<InputProps> = ({
  disabled,
  min,
  max,
  value,
  onChange,
  name,
  'data-testid': dataTest,
}) => {
  const { locale } = useIntl();

  const pickerLocale = useMemo(
    () => (locale ? locale.replace('_', '-') : 'en'),
    [locale],
  );

  const parseDateTime = (input?: string | number | Date): Date | null => {
    if (input == null || input === '') return null;

    if (input instanceof Date) return isValid(input) ? input : null;

    const raw = String(input);
    const parsed = parse(raw, DATE_TIME_FORMAT, new Date());

    if (isValid(parsed)) return parsed;

    const dateOnly = parse(raw, 'yyyy-MM-dd', new Date());
    return isValid(dateOnly) ? dateOnly : null;
  };

  const selectedDate = useMemo(
    () => parseDateTime(value as string | undefined),
    [value],
  );

  const handleChange = (date: Date | null) => {
    const nextValue = date && isValid(date) ? format(date, DATE_TIME_FORMAT) : '';
    const startDate = parseDateTime(min as string | undefined);

    if (startDate && isBefore(date!, startDate)) return;

    onChange?.({
      target: { name, value: nextValue },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <div style={{ flexGrow: 1, minWidth: 0 }}>
      <Picker
        disabled={disabled}
        value={selectedDate}
        onChange={handleChange}
        minDate={parseDateTime(min as string | undefined) ?? undefined}
        maxDate={parseDateTime(max as string | undefined) ?? undefined}
        locale={pickerLocale}
        format="dd/MM/yyyy HH:mm"
        disableClock
        clockProps={{
          renderNumbers: true,
        }}
        clearIcon={null}
        calendarIcon={<CalendarDotsIcon size={20} />}
        showLeadingZeros
        data-testid={dataTest || 'date-time-picker-input'}
      />
    </div>
  );
};

export default DateTimePicker;
